import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { DrawerSection, KeyValueGrid, MoneyValue } from '@shared/design-system/ui'
import type { ReactNode } from 'react'
import type { UserReceivingPlan } from '../model/user-receiving-plan'
import { planPercent } from '../model/user-receiving-plan'

export function PlanConditions({
  plan,
  reserveApplication,
}: {
  plan: UserReceivingPlan
  reserveApplication?: ReactNode
}) {
  const customFees = useFeatureFlag('customServiceFees')
  const terms = customFees ? plan.serviceFee : undefined
  const formula = `${planPercent(terms?.percentage ?? '9.9')} + US$ ${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(terms?.fixedUsd ?? '1.49'))}`
  const national = plan.fees.find((fee) => fee.currency === 'BRL')!
  const withdrawalFee = plan.withdrawalFees?.BRL ?? plan.withdrawalFeeBrl

  return (
    <>
      <DrawerSection
        title="Serviço VendePay"
        description={
          customFees
            ? 'Condição para novas vendas e novas assinaturas. Assinaturas existentes mantêm a taxa contratada. A parcela fixa em dólar é convertida em reais na cobrança.'
            : '9,9% + US$ 1,49 por cobrança, para qualquer venda. A parcela fixa é convertida em reais pela cotação registrada no momento da cobrança. Sem taxa de conversão.'
        }
      >
        <KeyValueGrid
          items={[
            {
              label: 'Taxa por cobrança',
              value: <span data-testid="receiving-plan-service-fee">{formula}</span>,
            },
          ]}
        />
      </DrawerSection>
      <DrawerSection
        title="Prazos internos de liberação"
        description="O país emissor do cartão orienta a referência. A data informada pela Stripe prevalece. Reserva e chegada ao banco têm condições próprias."
      >
        <KeyValueGrid
          items={[
            { label: 'Prazo interno do plano', value: `${plan.receivingDays} dias` },
            { label: 'Cartão emitido fora do Brasil', value: 'Cartão: referência T+5' },
            { label: 'Cartão emitido no Brasil', value: 'Cartão: referência T+30' },
          ]}
        />
      </DrawerSection>
      <DrawerSection
        title="Reserva configurada em reais"
        description="Condição solicitada para BRL, inclusive nas compras cobradas em outras moedas. A aplicação depende da confirmação da Stripe para cada usuário."
      >
        <KeyValueGrid
          items={[
            {
              label: 'Reserva configurada',
              value: `${planPercent(national.nationalReservePercentage)} por ${national.nationalReserveDays} ${national.nationalReserveDays === 1 ? 'dia' : 'dias'}`,
            },
          ]}
        />
      </DrawerSection>
      {reserveApplication}
      <DrawerSection
        title="Taxas configuradas no plano"
        description="Valores preservados para histórico e configuração do plano. Não representam cobrança automática nem tarifa Stripe. A tarifa efetiva só é observada na composição Stripe."
      >
        <KeyValueGrid
          items={[
            {
              label: 'Taxa configurada (BRL)',
              value: <MoneyValue amount={withdrawalFee} currency="BRL" size="sm" />,
            },
          ]}
        />
      </DrawerSection>
    </>
  )
}
